import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Snowflake, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'Cold Room Operators',
  'Freezer Workers',
  'Food Handling Specialists',
  'Quality Control Staff',
  'Packaging & Labelling Workers',
  'Inventory & Stock Rotation',
  'Sanitation & Cleaning Crews',
  'Forklift Operators (Cold-Rated)',
];

const benefits = [
  'Workers experienced in cold-environment safety',
  'Trained in food handling and HACCP protocols',
  'Available for all shifts including nights',
  'Full WCB and workplace safety compliance',
  'Scalable workforce for seasonal demand',
  'Fast placement for urgent staffing gaps',
];

const ColdStoragePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Cold Storage Staffing BC - WorkNation Labour Ltd | Cold Room Workers Vancouver</title>
        <meta name="description" content="WorkNation Labour Ltd provides cold storage and freezer staffing across BC. Cold room operators, food handlers, quality control, packaging workers. HACCP trained. Contact us for fast placement." />
        <link rel="canonical" href="https://worknation.ca/industries/cold-storage" />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-cyan-50 via-white to-sky-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Snowflake className="text-cyan-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-cyan-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Cold Storage Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd provides specialized cold storage and freezer workers to food distribution centres, cold chain facilities, and temperature-controlled warehouses across British Columbia. Our workers are trained in cold-environment safety and food handling protocols to keep your operation compliant and efficient.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="cold-storage-cta">
              Get Cold Storage Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Cold Storage Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Cold Storage Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cold storage and food distribution is a critical part of British Columbia&apos;s supply chain. From frozen food warehouses in Metro Vancouver to cold chain facilities supporting the agricultural sector, these operations require workers who can perform in demanding, temperature-controlled environments.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              WorkNation Labour Ltd specializes in placing workers who are experienced in cold-environment safety protocols, food handling procedures, and HACCP compliance. Our workers understand the unique challenges of working in freezer conditions and come prepared with appropriate gear and training.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you need workers for a short-term seasonal surge or ongoing cold storage operations, WorkNation provides reliable, trained staff who are ready to work from day one.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Cold Storage Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default ColdStoragePage;
